/**
 * Background AI Extraction Manager.
 *
 * Runs Gemini extraction jobs outside React component lifecycle so the
 * user can navigate to other patients while the AI processes. Results
 * are stored in memory keyed by patientId and applied when the user
 * returns to the patient file.
 *
 * Lifecycle:
 *   1. `submitExtraction(patientId, params)` — starts the job.
 *   2. Component can check `getJob(patientId)` on mount to see if a
 *      job is pending or has finished.
 *   3. `consumeResult(patientId)` — returns and clears the result.
 *   4. `clearJob(patientId)` — cancels/clears a job.
 *   5. `clearAll()` — called on logout.
 */

import { GeminiClient } from '@/services/gemini/client';
import type { AIAnalysisResult } from '@/types/ai';
import type { ExtractionTargetKey } from '@/utils/extractionTargets';

export interface ExtractionParams {
  audio: Blob | null;
  images: File[];
  pdfs: File[];
  text: string;
  /** Doctor's typed text, preserved verbatim in the final output. */
  doctorText: string;
  /**
   * Alvos escolhidos no quadro "o que a IA deve preencher". Viajam com o
   * job (que sobrevive à desmontagem do painel) pra que a aplicação do
   * resultado filtre pelos mesmos alvos. Ausente = extração legada (tudo).
   */
  targets?: ExtractionTargetKey[];
  /**
   * Linha do tempo de quem falou na gravação (spec 016, fase 2), medida
   * pelos microfones. Ausente = ninguém mediu (o prompt de sempre).
   */
  falantes?: string | null;
}

export type JobStatus = 'pending' | 'done' | 'error' | 'cancelled';

export interface ExtractionJob {
  patientId: string;
  status: JobStatus;
  result?: AIAnalysisResult;
  doctorText: string;
  /** Alvos selecionados quando o job foi disparado (ver ExtractionParams). */
  targets?: ExtractionTargetKey[];
  error?: string;
  startedAt: number;
  /** Controller usado para abortar a chamada HTTP da IA. */
  controller?: AbortController;
}

// In-memory store — lives as long as the browser tab is open.
const jobs = new Map<string, ExtractionJob>();

// Listeners notified when a job finishes (used by mounted components).
type Listener = (patientId: string, job: ExtractionJob) => void;
const listeners = new Set<Listener>();

export function onJobComplete(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function notifyListeners(patientId: string, job: ExtractionJob) {
  listeners.forEach((fn) => {
    try { fn(patientId, job); } catch { /* ignore */ }
  });
}

/** Start an extraction in the background. */
export function submitExtraction(
  patientId: string,
  params: ExtractionParams,
): void {
  // Aborta qualquer job pendente do mesmo paciente antes de iniciar outro.
  const previous = jobs.get(patientId);
  if (previous && previous.status === 'pending' && previous.controller) {
    try { previous.controller.abort(); } catch { /* ignore */ }
  }

  const controller = new AbortController();
  const job: ExtractionJob = {
    patientId,
    status: 'pending',
    doctorText: params.doctorText,
    targets: params.targets,
    startedAt: Date.now(),
    controller,
  };
  jobs.set(patientId, job);

  // Fire and forget — the promise updates the job in place.
  GeminiClient.analyzeMultimodal({
    audio: params.audio,
    images: params.images,
    pdfs: params.pdfs,
    text: params.text,
    action: 'transcribe',
    template: 'geral',
    // A gravação é a consulta (médico + paciente): não declarar "só 1 falante"
    // — a regra de quem disse vai no prompt (25/10/2026).
    interlocutors: 0,
    falantes: params.falantes,
    signal: controller.signal,
  })
    .then((result: AIAnalysisResult) => {
      const current = jobs.get(patientId);
      // Only update if this is still the same job (not cleared).
      if (current && current.startedAt === job.startedAt) {
        current.status = 'done';
        current.result = result;
        notifyListeners(patientId, current);
      }
    })
    .catch((err: unknown) => {
      const current = jobs.get(patientId);
      if (current && current.startedAt === job.startedAt) {
        // AbortError vira 'cancelled' (não 'error') pra que a UI saiba
        // distinguir e não exiba mensagem de falha.
        const name = (err as { name?: string })?.name;
        if (name === 'AbortError' || controller.signal.aborted) {
          current.status = 'cancelled';
          current.error = undefined;
        } else {
          current.status = 'error';
          current.error = (err as { message?: string })?.message || 'Erro desconhecido ao extrair.';
        }
        notifyListeners(patientId, current);
      }
    });
}

/**
 * Cancela um job em andamento. Aborta a chamada HTTP e marca o job como
 * 'cancelled'. Componente que está observando recebe a notificação.
 */
export function cancelJob(patientId: string): void {
  const current = jobs.get(patientId);
  if (!current) return;
  if (current.status !== 'pending') return;
  if (current.controller) {
    try { current.controller.abort(); } catch { /* ignore */ }
  }
  current.status = 'cancelled';
  current.error = undefined;
  notifyListeners(patientId, current);
}

/** Get the current job for a patient (if any). */
export function getJob(patientId: string): ExtractionJob | undefined {
  return jobs.get(patientId);
}

// ── Dedup de aplicação do resultado ──
// Um job concluído pode ser visto por MAIS DE UM caminho: o `onJobComplete`
// (enquanto montado) e o `consumeResult` (no mount). Como `onJobComplete` NÃO
// remove o job do mapa, ele fica como 'done' e a próxima montagem do painel o
// reaplicava via `consumeResult` — gerando auto-save DUPLICADO no feed. Este
// guard garante que o resultado de um job (chave = paciente + startedAt) seja
// aplicado UMA única vez, não importa quantos listeners/montagens o vejam.
const appliedResults = new Set<string>();

/**
 * Reivindica a aplicação do resultado de um job. Retorna `true` só para o
 * PRIMEIRO chamador (que deve aplicar/auto-salvar); chamadas seguintes para o
 * mesmo job retornam `false` e devem pular a aplicação.
 */
export function claimJobResult(patientId: string, startedAt: number): boolean {
  const key = `${patientId}:${startedAt}`;
  if (appliedResults.has(key)) return false;
  appliedResults.add(key);
  // Mantém o set pequeno (só os jobs recentes importam pro dedup).
  if (appliedResults.size > 50) {
    const oldest = appliedResults.values().next().value;
    if (oldest !== undefined) appliedResults.delete(oldest);
  }
  return true;
}

/** Consume (read + delete) a finished job's result. */
export function consumeResult(patientId: string): ExtractionJob | undefined {
  const job = jobs.get(patientId);
  if (job && (job.status === 'done' || job.status === 'error' || job.status === 'cancelled')) {
    jobs.delete(patientId);
    return job;
  }
  return undefined;
}

/** Clear a specific patient's job. */
export function clearJob(patientId: string): void {
  jobs.delete(patientId);
}

/** Clear all jobs (called on logout). */
export function clearAllJobs(): void {
  jobs.clear();
  listeners.clear();
  appliedResults.clear();
}
